import { AlertTriangle, Info, Search, RefreshCw, Globe } from 'lucide-react';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../AuthContext';
import SymbolCard from '../components/titan/SymbolCard';
import PremarketBanner from '../components/titan/PremarketBanner';
import UniverseStats from '../components/titan/UniverseStats';
import PatternUniverse from '../components/titan/PatternUniverse';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../components/ui/tooltip';

import { titanApi, patternsApi, ApiError } from '../lib/api';

// -- Types --

interface PatternSummary {
  id: string;
  name: string;
  description: string;
  criteria_count: number;
  is_active: boolean;
  gemini_confidence: number;
  tags: string[];
  asset_class?: 'stocks' | 'forex' | 'futures' | 'crypto';
  created_at: string;
  updated_at: string;
}

interface SymbolMetadata {
  sector?: string;
  industry?: string;
  price?: number;
  volume?: number;
  market_cap?: number;
  added_at?: string;
}

interface UniverseResponse {
  pattern_id: string;
  symbols: string[];
  symbol_count: number;
  symbol_metadata: Record<string, SymbolMetadata>;
  last_scan_at: string | null;
  total_scanned: number;
}

interface MergedSymbol {
  symbol: string;
  metadata: SymbolMetadata;
  patternNames: string[];
  isPremarket: boolean;
}

type SortField = 'symbol' | 'price' | 'volume' | 'market_cap';

const UniverseMonitor: React.FC = () => {
  const { getToken } = useAuth();

  // Data state
  const [patterns, setPatterns] = useState<PatternSummary[]>([]);
  const [universeMap, setUniverseMap] = useState<Record<string, UniverseResponse>>({});
  const [failedPatterns, setFailedPatterns] = useState<Set<string>>(new Set());
  const [watchlist, setWatchlist] = useState<string[]>([]);
  const [scanLoopRunning, setScanLoopRunning] = useState(false);
  const [lastScanAt, setLastScanAt] = useState<string | null>(null);

  // UI state
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshAllLoading, setRefreshAllLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [countdown, setCountdown] = useState(30);
  const [search, setSearch] = useState('');
  const [sectorFilter, setSectorFilter] = useState('');
  const [patternFilter, setPatternFilter] = useState('');
  const [sortBy, setSortBy] = useState<SortField>('volume');
  const [error, setError] = useState<string | null>(null);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // -- Data fetching --

  const fetchData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    setError(null);

    try {
      const [patternsData, statusData, watchlistData] = await Promise.all([
        patternsApi.list(),
        titanApi.status(),
        // Non-fatal: returns empty list when Titan controller isn't running (503)
        titanApi.premarketWatchlist().catch(() => ({ watchlist: [] })),
      ]);

      const allPatterns = patternsData?.patterns || [];
      setPatterns(allPatterns as any);
      setScanLoopRunning(statusData?.scan_loop_running ?? false);
      setLastScanAt(statusData?.last_scan_at ?? null);
      // Watchlist items can be strings OR objects with .symbol — normalize to strings
      const rawWatchlist = watchlistData?.watchlist || [];
      setWatchlist(rawWatchlist.map((item: any) => typeof item === 'string' ? item : item?.symbol || String(item)));

      const activeStock = allPatterns.filter(
        (p: any) => p.is_active && (!p.asset_class || p.asset_class === 'stocks')
      );

      const results = await Promise.allSettled(
        activeStock.map((p: any) =>
          patternsApi.universe(p.id).then((data) => ({
            patternId: p.id,
            data,
          }))
        )
      );

      const newUniverseMap: Record<string, UniverseResponse> = {};
      const newFailed = new Set<string>();

      results.forEach((result, idx) => {
        const patternId = activeStock[idx].id;
        if (result.status === 'fulfilled' && result.value.data) {
          newUniverseMap[patternId] = result.value.data as any;
        } else {
          newFailed.add(patternId);
        }
      });

      setUniverseMap(newUniverseMap);
      setFailedPatterns(newFailed);
    } catch (e: any) {
      const msg = e instanceof ApiError ? e.detail : (e?.message || 'Failed to load universe data');
      setError(msg);
      console.error('[UniverseMonitor] fetchData failed:', e);
    } finally {
      setInitialLoading(false);
      setRefreshing(false);
      setCountdown(30);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => fetchData(true), 30_000);
      countdownRef.current = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [autoRefresh, fetchData]);

  // -- Refresh all universes --

  const handleRefreshAll = useCallback(async () => {
    setRefreshAllLoading(true);
    try {
      const activeStock = patterns.filter(
        (p) => p.is_active && (!p.asset_class || p.asset_class === 'stocks')
      );

      for (const p of activeStock) {
        await patternsApi.universeRefresh(p.id);
      }

      await fetchData(true);
    } catch {
      setError('Failed to refresh universes');
    } finally {
      setRefreshAllLoading(false);
    }
  }, [patterns, fetchData]);

  // -- Merge symbols --

  const mergedSymbols: MergedSymbol[] = useMemo(() => {
    const symbolMap = new Map<string, MergedSymbol>();
    const watchlistSet = new Set(watchlist);
    const patternNameMap = new Map(patterns.map((p) => [p.id, p.name]));

    Object.entries(universeMap).forEach(([patternId, universe]) => {
      const patternName = patternNameMap.get(patternId) || patternId;

      universe.symbols.forEach((sym) => {
        const existing = symbolMap.get(sym);
        const meta = universe.symbol_metadata?.[sym] || {};

        if (existing) {
          if (!existing.patternNames.includes(patternName)) {
            existing.patternNames.push(patternName);
          }
          if (meta.price && !existing.metadata.price) existing.metadata.price = meta.price;
          if (meta.volume && !existing.metadata.volume) existing.metadata.volume = meta.volume;
          if (meta.market_cap && !existing.metadata.market_cap) existing.metadata.market_cap = meta.market_cap;
          if (meta.sector && !existing.metadata.sector) existing.metadata.sector = meta.sector;
        } else {
          symbolMap.set(sym, {
            symbol: sym,
            metadata: { ...meta },
            patternNames: [patternName],
            isPremarket: watchlistSet.has(sym),
          });
        }
      });
    });

    return Array.from(symbolMap.values());
  }, [universeMap, watchlist, patterns]);

  // -- Filtering + sorting --

  const allSectors = useMemo(() => {
    const sectors = new Set<string>();
    mergedSymbols.forEach((s) => {
      if (s.metadata.sector) sectors.add(s.metadata.sector);
    });
    return Array.from(sectors).sort();
  }, [mergedSymbols]);

  const activePatternNames = useMemo(
    () =>
      patterns
        .filter((p) => p.is_active && (!p.asset_class || p.asset_class === 'stocks'))
        .map((p) => p.name),
    [patterns]
  );

  const filteredSymbols = useMemo(() => {
    let result = mergedSymbols;

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (s) =>
          s.symbol.toLowerCase().includes(q) ||
          (s.metadata.sector || '').toLowerCase().includes(q) ||
          (s.metadata.industry || '').toLowerCase().includes(q)
      );
    }

    if (sectorFilter) {
      result = result.filter((s) => s.metadata.sector === sectorFilter);
    }

    if (patternFilter) {
      result = result.filter((s) => s.patternNames.includes(patternFilter));
    }

    result.sort((a, b) => {
      switch (sortBy) {
        case 'symbol':
          return a.symbol.localeCompare(b.symbol);
        case 'price':
          return (b.metadata.price || 0) - (a.metadata.price || 0);
        case 'volume':
          return (b.metadata.volume || 0) - (a.metadata.volume || 0);
        case 'market_cap':
          return (b.metadata.market_cap || 0) - (a.metadata.market_cap || 0);
        default:
          return 0;
      }
    });

    return result;
  }, [mergedSymbols, search, sectorFilter, patternFilter, sortBy]);

  const activeStockPatterns = useMemo(
    () => patterns.filter((p) => !p.asset_class || p.asset_class === 'stocks'),
    [patterns]
  );

  const uniqueSectorCount = allSectors.length;

  // -- Render --

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Progress bar during refresh */}
      {(refreshing || refreshAllLoading) && (
        <Progress value={undefined} className="h-1" />
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <Globe className="h-6 w-6 text-primary" />
            <h1 className="font-display text-xl sm:text-2xl font-bold text-foreground">Universe Monitor</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Active securities being scanned for trade execution
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch
              checked={autoRefresh}
              onCheckedChange={(checked) => {
                setAutoRefresh(checked);
                if (checked) setCountdown(30);
              }}
            />
            <Label className="text-sm text-muted-foreground">
              Auto {autoRefresh ? `(${countdown}s)` : 'off'}
            </Label>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  onClick={handleRefreshAll}
                  disabled={refreshAllLoading}
                >
                  <RefreshCw className={`h-4 w-4 mr-1.5 ${refreshAllLoading ? 'animate-spin' : ''}`} />
                  {refreshAllLoading ? 'Refreshing...' : 'Refresh All'}
                </Button>
              </TooltipTrigger>
              <TooltipContent>Refresh all universes (triggers rebuild)</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Error */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Stats row */}
      <UniverseStats
        totalSymbols={mergedSymbols.length}
        activeBots={patterns.filter((p) => p.is_active && (!p.asset_class || p.asset_class === 'stocks')).length}
        lastScanAt={lastScanAt}
        scanLoopRunning={scanLoopRunning}
        loading={initialLoading}
      />

      {/* Premarket banner */}
      <PremarketBanner watchlist={watchlist} />

      {/* Filter controls */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-64">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search ticker or sector..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="w-44">
          <Label className="mb-1.5 block text-sm">Sector</Label>
          <Select value={sectorFilter || '_all'} onValueChange={(v) => setSectorFilter(v === '_all' ? '' : v)}>
            <SelectTrigger>
              <SelectValue placeholder="All Sectors" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All Sectors</SelectItem>
              {allSectors.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-44">
          <Label className="mb-1.5 block text-sm">Pattern</Label>
          <Select value={patternFilter || '_all'} onValueChange={(v) => setPatternFilter(v === '_all' ? '' : v)}>
            <SelectTrigger>
              <SelectValue placeholder="All Patterns" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">All Patterns</SelectItem>
              {activePatternNames.map((n) => (
                <SelectItem key={n} value={n}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-44">
          <Label className="mb-1.5 block text-sm">Sort by</Label>
          <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortField)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="volume">Volume</SelectItem>
              <SelectItem value="price">Price</SelectItem>
              <SelectItem value="market_cap">Market Cap</SelectItem>
              <SelectItem value="symbol">Symbol (A-Z)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {(search || sectorFilter || patternFilter) && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setSearch('');
              setSectorFilter('');
              setPatternFilter('');
            }}
          >
            Clear filters
          </Button>
        )}
      </div>

      {/* Symbol Grid */}
      {initialLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {Array.from({ length: 12 }).map((_, i) => (
            <Skeleton key={i} className="h-[200px] rounded-lg" />
          ))}
        </div>
      ) : filteredSymbols.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Info className="h-10 w-10 text-muted-foreground mb-3" />
          <p className="text-lg font-medium text-foreground">
            {mergedSymbols.length === 0
              ? 'No universe data yet'
              : 'No symbols match your filters'}
          </p>
          <p className="text-sm text-muted-foreground mt-1 max-w-md">
            {mergedSymbols.length === 0
              ? 'Make sure your patterns have universe_enabled: true and have completed at least one scan.'
              : 'Try adjusting your search, sector, or pattern filters.'}
          </p>
        </div>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            Showing {filteredSymbols.length} of {mergedSymbols.length} securities
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredSymbols.map((sym) => (
              <div key={sym.symbol}>
                <SymbolCard
                  symbol={sym.symbol}
                  metadata={sym.metadata}
                  patternNames={sym.patternNames}
                  isPremarket={sym.isPremarket}
                />
              </div>
            ))}
          </div>
        </>
      )}

      {/* Per-Pattern Accordion Section */}
      {!initialLoading && activeStockPatterns.length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-foreground mb-4">By Pattern</h3>
          <Accordion type="multiple">
            {activeStockPatterns.map((p) => {
              const universe = universeMap[p.id];
              const failed = failedPatterns.has(p.id);
              const symbolCount = universe?.symbol_count ?? 0;

              return (
                <AccordionItem key={p.id} value={p.id} className="rounded-lg border bg-card mb-2">
                  <AccordionTrigger className="px-4 hover:no-underline">
                    <div className="flex items-center gap-2 flex-1">
                      <span className="font-medium">{p.name}</span>
                      <Badge variant={p.is_active ? 'default' : 'secondary'} className="text-[10px]">
                        {p.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                      {failed && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger>
                              <AlertTriangle className="h-4 w-4 text-rose-500" />
                            </TooltipTrigger>
                            <TooltipContent>Failed to load universe</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      {symbolCount === 0 && p.is_active && !failed && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger
                              onClick={async (e) => {
                                e.stopPropagation();
                                await patternsApi.universeRefresh(p.id);
                                fetchData(true);
                              }}
                            >
                              <AlertTriangle className="h-4 w-4 text-amber-500" />
                            </TooltipTrigger>
                            <TooltipContent>Empty universe -- click to refresh</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                      <span className="ml-auto mr-2 text-sm text-muted-foreground">
                        {symbolCount} symbols
                      </span>
                      {universe?.last_scan_at && (
                        <span className="text-xs text-muted-foreground">
                          Scanned {new Date(universe.last_scan_at).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4">
                    <PatternUniverse patternId={p.id} patternName={p.name} isActive={p.is_active} />
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      )}

      {/* Footer Stats */}
      {!initialLoading && (
        <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground pt-4 border-t border-border">
          <span>Total symbols: {mergedSymbols.length}</span>
          <span>Unique sectors: {uniqueSectorCount}</span>
          <span>Last refresh: {new Date().toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit' })}</span>
          <span>Data source: EODHD + Alpaca</span>
        </div>
      )}
    </div>
  );
};

export default UniverseMonitor;
